import { create } from 'zustand';

export type UserRole = 'customer' | 'store' | 'delivery_agent' | 'admin';

export interface User {
  id: number;
  email: string;
  phone?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => {
  // Try to restore auth state from localStorage
  const storedAuth = typeof window !== 'undefined' ? localStorage.getItem('auth') : null;
  const initialState = storedAuth ? JSON.parse(storedAuth) : { user: null, accessToken: null, refreshToken: null };

  return {
    user: initialState.user || null,
    accessToken: initialState.accessToken || null,
    refreshToken: initialState.refreshToken || null,
    isLoading: false,
    error: null,
    isAuthenticated: !!initialState.accessToken,

    setUser: (user) => {
      set((state) => {
        const newState = { ...state, user };
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth', JSON.stringify({
            user,
            accessToken: state.accessToken,
            refreshToken: state.refreshToken,
          }));
        }
        return newState;
      });
    },

    setTokens: (accessToken, refreshToken) => {
      set((state) => {
        const newState = {
          ...state,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        };
        if (typeof window !== 'undefined') {
          localStorage.setItem('auth', JSON.stringify({
            user: state.user,
            accessToken,
            refreshToken,
          }));
        }
        return newState;
      });
    },

    setLoading: (loading) => set({ isLoading: loading }),

    setError: (error) => set({ error }),

    logout: () => {
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        error: null,
      });
      if (typeof window !== 'undefined') {
        localStorage.removeItem('auth');
        // Wipe persisted client-side caches that are scoped to the
        // logged-in user. Without this the next user (or the same user
        // logging back in) sees stale data — e.g. a restaurant cart
        // from the previous session — which surfaces as "old data in
        // cart". The restaurant cart is a zustand `persist` store
        // keyed `pm-restaurant-cart` (see stores/restaurantCart.ts).
        try { localStorage.removeItem('pm-restaurant-cart'); } catch { /* ignore */ }
      }
    },

    clearError: () => set({ error: null }),
  };
});
